import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon,
} from '@mui/icons-material';

import Loading from '../components/common/Loading';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      await login(email, senha);
      navigate('/dashboard');
    } catch (err) {
      setError('Email ou senha incorretos');
      setLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <div>
          {/* Header */}
          <Box textAlign="center" mb={5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ width: 80, height: 80 }}>
                <img
                  src="/Logo_verso_46x46.svg"
                  alt="Logo Verso"
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography
                variant="h1"
                fontWeight={700}
                color="primary.main"
                sx={{ fontSize: '80px', lineHeight: '80px' }}
              >
                Verso
              </Typography>
            </Box>
          </Box>

          {/* Login Card */}
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: theme =>
                `0 10px 40px ${alpha(
                  theme.palette.common.black,
                  theme.palette.mode === 'dark' ? 0.3 : 0.1
                )}`,
              border: theme =>
                `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              maxWidth: 420,
              width: '100%',
              mx: 'auto',
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h4"
                fontWeight={600}
                color="text.primary"
                gutterBottom
              >
                Entrar
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Acesse sua conta para continuar.
              </Typography>
            </Box>

            {/* Form Content */}
            <CardContent sx={{ px: 4, pb: 4, pt: 0 }}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {/* Erro geral */}
                  {error && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: theme => alpha(theme.palette.error.main, 0.1),
                        border: theme =>
                          `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="error.main"
                        fontWeight={500}
                      >
                        {error}
                      </Typography>
                    </Box>
                  )}

                  {/* Email */}
                  <TextField
                    label="Email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    autoComplete="email"
                  />

                  {/* Senha */}
                  <TextField
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    variant="outlined"
                    value={senha}
                    onChange={e => setSenha(e.target.value)}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={toggleShowPassword} edge="end">
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Login Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <Loading
                          variant="minimal"
                          size="small"
                          color="inherit"
                        />
                      ) : (
                        <LoginIcon />
                      )
                    }
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Verso - Texto e Forma Conteúdo Educacional. Todos os
              direitos reservados.
            </Typography>
            <Box mt={7} display="flex" justifyContent="center">
              <img
                src={theme.palette.mode === 'dark' 
                  ? '/PoweredByAWS_horiz_RGB_1c_White.svg'
                  : '/PoweredByAWS_horiz_RGB_1c_Gray850.svg'
                }
                alt="Powered by AWS"
                style={{ height: '32px', width: 'auto' }}
              />
            </Box>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

export default LoginPage;
